import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NgApexchartsModule } from 'ng-apexcharts';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { BaseChartOptions, DEFAULT_CHART_OPTIONS } from 'shared/types/chart.config';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { BalanceService } from 'core/services/balance.service';
import { formatBalanceDate, formatBalanceMoney } from 'shared/utils/balance-format.utils';
import { BalanceMoneyPipe } from 'shared/pipes/balance-money.pipe';

interface IMonthlySpendingTrend {
  direction: 'up' | 'down';
  percentage: string;
}

// Whole percentages with a comma thousands separator (13,829%) in EN and ES.
const trendPercentageFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

@Component({
  selector: 'app-card-details',
  standalone: true,
  imports: [
    ResourceLoadErrorComponent,
    TranslatePipe,
    NzCollapseModule,
    NgApexchartsModule,
    KorviuButtonComponent,
    BreadcrumbComponent,
    BalanceMoneyPipe
  ],
  templateUrl: './card-details.component.html',
  styleUrls: ['./card-details.component.scss']
})
export class CardDetailsComponent {
  readonly #balanceService = inject(BalanceService);
  readonly #translateService = inject(TranslateService);

  readonly language = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  });

  readonly memberSummaryCardsResource = this.#balanceService.getMemberSummaryCards();
  readonly cardSpending = computed(() =>
    this.memberSummaryCardsResource.hasValue() ? this.memberSummaryCardsResource.value()?.card_spending : undefined
  );

  readonly earningsTableResource = this.#balanceService.getEarningsTable(6, this.language);
  readonly monthlySpendingPeriods = computed(() =>
    this.earningsTableResource.hasValue() ? this.earningsTableResource.value()?.periods.slice(-6) || [] : []
  );
  readonly monthlySpendingTrend = computed<IMonthlySpendingTrend | null>(() => {
    const periods = this.monthlySpendingPeriods();
    // The API sends decimal strings; compare them as numbers ("12593.10" < "90.41" as strings).
    const current = toAmount(periods.at(-1)?.my_spending_usd);
    const previous = toAmount(periods.at(-2)?.my_spending_usd);

    if (current === null || previous === null || previous === 0 || current === previous) {
      return null;
    }

    const percentage = Math.round(Math.abs((current - previous) / previous) * 100);
    if (percentage === 0) {
      return null;
    }

    return {
      direction: current > previous ? 'up' : 'down',
      percentage: trendPercentageFormatter.format(percentage)
    };
  });

  public readonly earningsGrowthChartOptions = computed<BaseChartOptions>(() => {
    const locale = this.language();
    // USD amounts use the same $1,234.56 format in EN and ES.
    const formatUsd = (value: number | string) => formatBalanceMoney(value);
    const periods = this.monthlySpendingPeriods();
    const categories = periods.map(period => formatBalanceDate(period.period_date, locale, 'month'));
    const seriesData = periods.map(period => toAmount(period.my_spending_usd) ?? 0);
    const barHeight = 32;
    const gap = 0;
    const numberOfBars = categories.length;
    const chartHeight = barHeight * numberOfBars + gap * (numberOfBars - 1);

    // Calculate mobile chart height
    const mobileBarHeight = 36; // Увеличиваем высоту бара для мобильных
    const mobileGap = 32; // Добавляем отступы между барами
    const mobileChartHeight = mobileBarHeight * numberOfBars + mobileGap * (numberOfBars - 1);

    return {
      ...DEFAULT_CHART_OPTIONS,
      chart: {
        ...DEFAULT_CHART_OPTIONS.chart,
        type: 'bar',
        height: chartHeight,
        width: '100%'
      },
      states: {
        /* normal: {
          filter: {
            type: 'none'
          }
        }, */
        hover: {
          filter: {
            type: 'none'
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none'
          }
        }
      },
      series: [
        {
          name: 'Earnings',
          data: seriesData
        }
      ],
      xaxis: {
        ...DEFAULT_CHART_OPTIONS.xaxis,
        categories,
        labels: {
          show: true,
          rotate: 0,
          hideOverlappingLabels: true,
          trim: true,
          style: {
            fontSize: '14px',
            colors: '#737980',
            fontFamily: 'Outfit'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        ...DEFAULT_CHART_OPTIONS.yaxis,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },

      plotOptions: {
        ...DEFAULT_CHART_OPTIONS.plotOptions,
        bar: {
          ...DEFAULT_CHART_OPTIONS.plotOptions.bar,
          horizontal: false,
          borderRadius: 4,
          columnWidth: '48px',
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        ...DEFAULT_CHART_OPTIONS.dataLabels,
        enabled: true,
        offsetY: -25,
        formatter: formatUsd,
        style: {
          fontSize: '16px',
          fontFamily: 'Outfit',
          fontWeight: '400',
          colors: ['#505760']
        }
      },
      tooltip: {
        ...DEFAULT_CHART_OPTIONS.tooltip,
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex][dataPointIndex] ?? 0;
          const category = w.config.xaxis?.categories[dataPointIndex] ?? '';
          const displayValue = formatUsd(value);
          return `
					<div class="px-3 py-2 flex flex-col custom-tooltip">
					  <div class="text-sm md:text-base font-medium outfit title">${category}</div>
					  <div class="paragraph-small-card font-normal outfit value">${displayValue}</div>
					</div>
				  `;
        }
      },
      responsive: [
        {
          breakpoint: 540,
          options: {
            chart: {
              height: mobileChartHeight // Добавляем явную высоту для мобильного графика
            },
            plotOptions: {
              bar: {
                horizontal: true,
                barHeight: `${mobileBarHeight}px` // Увеличиваем высоту бара
              }
            },
            dataLabels: {
              enabled: true,
              offsetX: 35,
              offsetY: 0,
              formatter: formatUsd,
              style: {
                fontSize: '14px',
                fontFamily: 'Outfit',
                fontWeight: '400',
                colors: ['#505760']
              }
            },
            xaxis: {
              labels: {
                show: false
              }
            },
            yaxis: {
              labels: {
                show: true,
                style: {
                  fontSize: '14px',
                  fontFamily: 'Outfit'
                }
              }
            }
          }
        }
      ]
    };
  });

  activePanels: boolean[] = [true, false, false, false];

  onPanelChange(index: number, isActive: boolean): void {
    if (isActive) {
      this.activePanels = this.activePanels.map(() => false);
      this.activePanels[index] = true;
    }
  }

  openReloadLocations(): void {
    window.open('https://secure.attheregister.com/locations', '_blank');
  }

  openDirectDepositForm(): void {
    const pdfUrl = 'assets/pdf/DDAform.pdf';
    window.open(pdfUrl, '_blank');
  }
}

function toAmount(value: number | string | null | undefined): number | null {
  const amount = Number(value);
  return value == null || value === '' || !Number.isFinite(amount) ? null : amount;
}
